import { RestError } from '@azure/core-http';
import {
  CarrierDto,
  KnownSortOrder,
  CarriersControllerGetOptionalParams,
  CarrierHierarchyDto,
  CarriersControllerListOptionalParams,
  CreateCarrierDto,
  EditCarrierDto,
  EffectiveServicingDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';
import { REFERENCE_LIST_STALE_TIME } from '../../core/queries/query-helpers';
import {
  CARRIERS_PAGE_SIZE,
  CarriersListQueryParams,
  makeCarriersListQueryParams
} from './list/logic/carriers-list-query.map';

export function useQueryListCarriers(
  projectID?: string,
  options?: CarriersControllerListOptionalParams & { enabled?: boolean }
) {
  const api = useApi();

  const { enabled, ...filters } = options || {};

  return useQuery(
    [QueryKeys.CARRIERS, projectID],
    async () => {
      if (!projectID) {
        return {
          items: []
        };
      }
      return await api.carriersController.list(projectID, false, {
        pageNo: 1,
        perPageRecords: 10000,
        sortBy: 'name',
        ...filters
      });
    },
    {
      enabled: options?.enabled !== false,
      staleTime: REFERENCE_LIST_STALE_TIME
    }
  );
}

/**
 * `GET /admin/carriers`, paged for the list screen. Kept separate from `useQueryListCarriers`,
 * whose single-page shape three other screens depend on.
 *
 * Scope is decided entirely by `projectID`: the endpoint sweeps servicing grants across that
 * project, its ancestors and its descendants. Nothing in scope means zero rows, not an error.
 */
export function useInfiniteQueryCarriersList(projectID: string | undefined, params: Partial<CarriersListQueryParams>) {
  const api = useApi();

  const { includeDeleted, ...queryParams } = makeCarriersListQueryParams(params);

  return useInfiniteQuery(
    [QueryKeys.CARRIERS, QueryKeys.LIST, projectID, params],
    async ({ pageParam = 1 }) => {
      if (!projectID) {
        return {
          items: [] as CarrierDto[],
          totalRecords: 0,
          perPageRecords: CARRIERS_PAGE_SIZE,
          totalPages: 1,
          pageNo: 1
        };
      }

      // `includeDeleted` is positional on this endpoint, not one of the options.
      const result = await api.carriersController.list(projectID, includeDeleted === true, {
        ...queryParams,
        perPageRecords: CARRIERS_PAGE_SIZE,
        pageNo: pageParam
      });

      return { ...result, items: result.items || [] };
    },
    {
      enabled: !!projectID,
      getNextPageParam(lastPage) {
        return lastPage.pageNo < lastPage.totalPages ? lastPage.pageNo + 1 : undefined;
      }
    }
  );
}

/**
 * One carrier. `projectID` narrows its capabilities to that project's scope — a carrier working in
 * two networks would otherwise report the other's permissions — so the detail screen passes the
 * project it was reached through.
 */
/**
 * Notes on the carrier group. A carrier is a group, so the groups notes controller already serves
 * them — notes on its couriers are theirs, and are read from the courier.
 */
export function useInfiniteQueryCarrierNotesList(carrierID: string, sortOrder?: KnownSortOrder) {
  const api = useApi();

  return useInfiniteQuery(
    [QueryKeys.CARRIERS, carrierID, QueryKeys.NOTES, sortOrder],
    async ({ pageParam = 1 }) => {
      return await api.groupsNotesController.findAll(carrierID, {
        sortOrder,
        perPageRecords: 5,
        pageNo: pageParam
      });
    },
    {
      getNextPageParam(lastPage) {
        return lastPage.pageNo < lastPage.totalPages ? lastPage.pageNo + 1 : undefined;
      }
    }
  );
}

/** Settings is refreshed as carriers are nodes in its tree. */
export function useMutationCreateCarrier() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CarrierDto, RestError, CreateCarrierDto>(async (dto) => await api.carriersController.create(dto), {
    onSuccess() {
      queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      queryClient.invalidateQueries(QueryKeys.SETTINGS);
    }
  });
}

export function useMutationEditCarrier(carrierID: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CarrierDto, RestError, EditCarrierDto>(
    async (dto) => await api.carriersController.edit(carrierID, dto),
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}

/** For a carrier only known at save time, such as a newly chosen registration target. */
export function useMutationEditCarrierByID() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CarrierDto, RestError, { carrierID: string; dto: EditCarrierDto }>(
    async ({ carrierID, dto }) => await api.carriersController.edit(carrierID, dto),
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}

/** Clears the review flag. Nothing sets it again. */
export function useMutationApproveCarrier() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<CarrierDto, RestError, string>(
    async (carrierID) => await api.carriersController.approve(carrierID),
    {
      onSuccess() {
        queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      }
    }
  );
}

/** Soft delete, here and in AC. Settings is refreshed as carriers are nodes in its tree. */
export function useMutationDeleteCarrier() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, RestError, string>(async (carrierID) => await api.carriersController.delete(carrierID), {
    onSuccess() {
      queryClient.invalidateQueries([QueryKeys.CARRIERS]);
      queryClient.invalidateQueries(QueryKeys.SETTINGS);
    }
  });
}

export function useQueryGetCarrier(carrierID?: string, options?: CarriersControllerGetOptionalParams) {
  const api = useApi();

  return useQuery<CarrierDto | undefined, RestError>(
    [QueryKeys.CARRIERS, carrierID, options?.projectID, options?.includeCapabilities],
    async () => {
      if (!carrierID) {
        return undefined;
      }
      return await api.carriersController.get(carrierID, options);
    },
    { enabled: !!carrierID }
  );
}

/**
 * The carrier hierarchy from one project: the company to label the create-courier form with, the
 * units a courier may be added to, and which to preselect.
 *
 * Positional, not permission-based: the units are those managed by this project or one beneath it,
 * so a project deeper in the company answers with less than the company's own. Ask with the widest
 * project the caller can read, not merely the one they arrived through.
 */
export function useQueryGetCarrierHierarchy(projectID?: string, options?: { enabled?: boolean }) {
  const api = useApi();

  return useQuery<CarrierHierarchyDto | undefined, RestError>(
    [QueryKeys.CARRIERS, projectID, QueryKeys.HIERARCHY],
    async () => {
      if (!projectID) {
        return undefined;
      }
      return await api.carrierOrganisationsController.getHierarchy(projectID);
    },
    { enabled: !!projectID && options?.enabled !== false }
  );
}

/**
 * Everywhere a group may operate: its own grants plus anything inherited from a container above it.
 * An operating unit usually holds none of its own and works entirely through its organisation's,
 * so listing only its own edges would report it as servicing nowhere.
 */
export function useQueryListEffectiveServicing(groupID?: string) {
  const api = useApi();

  return useQuery<EffectiveServicingDto[], RestError>(
    [QueryKeys.CARRIERS, groupID, QueryKeys.SERVICING],
    async () => {
      if (!groupID) {
        return [];
      }
      return await api.carrierOrganisationsController.listServicing(groupID);
    },
    { enabled: !!groupID }
  );
}
